import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put, patch as apiPatch } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Skeleton } from '@/components/atoms/Skeleton';
import { RichTextEditor } from '@/components/organisms/RichTextEditor';
import { toast } from '@/store/toastStore';

/**
 * 어드민 공지 작성/수정 페이지 (모달 → 페이지 전환).
 *
 * 데이터 모델: sisegonggan.posts (board_id=12 운영/공지) + is_pinned + pin_order
 *  - 어드민의 공지는 운영/공지 게시판 상단(고정)에 노출됨
 *  - 사이트-와이드 배너(sisegonggan.notices) 와는 분리됐음 (메인 NoticeBanner 제거)
 *
 * 라우트:
 *  - /admin/notices/new
 *  - /admin/notices/:id/edit
 */

interface PostForEdit {
  id: number;
  board_id: number;
  title: string;
  content: string;
  is_pinned: boolean;
  pin_order: number | null;
  user_id: string;
}

interface PinnedPost {
  id: number;
  title: string;
  pin_order: number;
}

const NOTICE_OPS_BOARD_ID = 12;

export function NoticeFormPage() {
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const editId = params.id ? Number(params.id) : null;
  const isEdit = editId !== null && !Number.isNaN(editId);
  const qc = useQueryClient();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [pinOrder, setPinOrder] = useState<number | ''>('');
  const [hydrated, setHydrated] = useState(false);

  /* 편집 모드 — 기존 게시글 로드 */
  const editQ = useQuery({
    queryKey: ['admin', 'notice', 'edit', editId ?? -1],
    queryFn: () => get<PostForEdit>(`/posts/${editId}`),
    enabled: isEdit,
  });
  useEffect(() => {
    if (!isEdit || hydrated) return;
    const d = editQ.data;
    if (!d) return;
    if (d.board_id !== NOTICE_OPS_BOARD_ID) {
      toast.error('운영/공지 게시판 글이 아니에요.');
      navigate('/admin/notices', { replace: true });
      return;
    }
    setTitle(d.title);
    setContent(d.content);
    setIsPinned(!!d.is_pinned);
    setPinOrder(d.pin_order ?? '');
    setHydrated(true);
  }, [isEdit, editQ.data, hydrated, navigate]);

  /* 현재 고정글 목록 — pinOrder 드롭다운 옵션 */
  const pinnedQ = useQuery({
    queryKey: ['posts', 'pinned'],
    queryFn: () => get<PinnedPost[]>('/posts/pinned'),
    staleTime: 60_000,
  });
  const pinned = pinnedQ.data ?? [];
  // 본인 글이 이미 고정 중이면 그건 +1 카운트하지 않음 (자기 자리 변경)
  const otherPinnedCount =
    isEdit && editId
      ? pinned.filter((p) => p.id !== editId).length
      : pinned.length;
  const maxPosition = Math.min(otherPinnedCount + 1, 5);
  const positions = Array.from({ length: maxPosition }, (_, i) => i + 1);

  const submitMut = useMutation({
    mutationFn: async () => {
      if (isEdit && editId) {
        // 1. 본문 PUT — title/content 만 변경 (boardId 변경 불가)
        await put(`/posts/${editId}`, { title, content });
        // 2. 고정 상태 동기화 — PATCH /posts/:id/pin
        const wasPinned = !!editQ.data?.is_pinned;
        if (wasPinned !== isPinned) {
          await apiPatch(`/posts/${editId}/pin`, { pinned: isPinned });
        }
        // 3. 고정 순서 동기화 — 핀 ON + 순서 지정 시
        if (isPinned && pinOrder !== '') {
          await apiPatch(`/posts/${editId}/pin-order`, { pinOrder });
        }
        return { id: editId };
      }
      // 신규 — POST /posts (server 가 admin + board=12 + isPinned 처리)
      const body: Record<string, unknown> = {
        boardId: NOTICE_OPS_BOARD_ID,
        title,
        content,
        isPinned,
      };
      if (isPinned && pinOrder !== '') body.pinOrder = pinOrder;
      return post<{ id: number }>('/posts', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'posts'] });
      qc.invalidateQueries({ queryKey: ['posts', 'pinned'] });
      qc.invalidateQueries({ queryKey: ['admin', 'notices', 'list'] });
      toast.success(isEdit ? '수정 완료' : '등록 완료');
      navigate(`/admin/notices`, { replace: true });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message ?? err?.message ?? '저장 실패');
    },
  });

  if (isEdit && editQ.isLoading) {
    return (
      <div className="w-full">
        <Skeleton className="mb-4 h-9 w-1/3" />
        <Skeleton className="h-72" />
      </div>
    );
  }

  const canSubmit = title.trim().length >= 1 && content.trim().length >= 1;

  return (
    <div className="w-full">
      <div className="mb-6 flex items-center justify-between gap-2">
        <h1 className="text-2xl font-extrabold">
          {isEdit ? '공지 수정' : '새 공지 작성'}
        </h1>
        <Button variant="ghost" onClick={() => navigate('/admin/notices')}>
          목록으로
        </Button>
      </div>

      <div className="mb-4 rounded-md border border-[var(--color-border)] bg-[var(--color-info-bg)] p-3 text-xs text-[var(--color-info)]">
        등록한 공지는 <strong>운영/공지 게시판</strong>에 작성되며,
        고정으로 등록하면 <strong>모든 게시판 상단의 공지 배너</strong>에 노출됩니다.
      </div>

      <div className="space-y-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
        <div>
          <label className="mb-1 block text-sm font-medium text-[var(--color-text)]">
            제목 <span className="text-[var(--color-fall)]">*</span>
          </label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="공지 제목"
            maxLength={200}
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-[var(--color-text)]">
            내용 <span className="text-[var(--color-fall)]">*</span>
          </label>
          <RichTextEditor
            value={content}
            onChange={setContent}
            placeholder="공지 내용을 입력하세요 (제목은 H2/H3, 본문은 일반 단락)"
          />
        </div>

        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3">
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isPinned}
              onChange={(e) => {
                setIsPinned(e.target.checked);
                if (!e.target.checked) setPinOrder('');
              }}
              className="h-4 w-4 rounded accent-[var(--color-primary)]"
            />
            <span className="font-medium">게시판 상단에 고정</span>
            <span className="text-xs text-[var(--color-text-sub)]">
              · 최대 5개, 모든 게시판 상단 배너에 노출
            </span>
          </label>

          {isPinned && (
            <div className="mt-3">
              <label className="mb-1.5 block text-sm font-medium">고정 순서</label>
              <select
                value={pinOrder}
                onChange={(e) =>
                  setPinOrder(e.target.value === '' ? '' : Number(e.target.value))
                }
                className="h-10 w-full max-w-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm"
              >
                <option value="">자동 (마지막 순서로 추가)</option>
                {positions.map((p) => (
                  <option key={p} value={p}>
                    {p}번째
                    {p <= otherPinnedCount && pinned[p - 1]
                      ? ` (기존: ${pinned[p - 1].title.slice(0, 24)}…)`
                      : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-[11px] text-[var(--color-text-sub)]">
                현재 고정 {otherPinnedCount}개 / 최대 5개. 선택한 위치에 삽입되고 이후 글은 한 칸씩 밀립니다.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => navigate('/admin/notices')}>
            취소
          </Button>
          <Button
            disabled={!canSubmit}
            loading={submitMut.isPending}
            onClick={() => submitMut.mutate()}
          >
            {isEdit ? '수정 완료' : '등록'}
          </Button>
        </div>
      </div>
    </div>
  );
}
